import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { LayoutDashboard, CalendarDays, Building2, Megaphone, Users, Settings, ExternalLink, Images } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/lib/i18n";
import api from "@/lib/api";
import { AdminOverview } from "@/components/admin/AdminOverview";
import { AdminEvents } from "@/components/admin/AdminEvents";
import { AdminMosques } from "@/components/admin/AdminMosques";
import { AdminAnnouncements } from "@/components/admin/AdminAnnouncements";
import { AdminMembers } from "@/components/admin/AdminMembers";
import { AdminSettings } from "@/components/admin/AdminSettings";
import { AdminGallery } from "@/components/admin/AdminGallery";

const TABS = [
  { id: "overview", icon: LayoutDashboard, label: "overview", roles: ["association_admin"] },
  { id: "events", icon: CalendarDays, label: "events", roles: ["association_admin", "mosque_admin"] },
  { id: "mosques", icon: Building2, label: "mosques", roles: ["association_admin", "mosque_admin"] },
  { id: "announcements", icon: Megaphone, label: "announcements", roles: ["association_admin", "moderator"] },
  { id: "gallery", icon: Images, label: "gallery", roles: ["association_admin", "moderator"] },
  { id: "members", icon: Users, label: "members", roles: ["association_admin", "mosque_admin"] },
  { id: "settings", icon: Settings, label: "settings", roles: ["association_admin"] },
];

export default function AdminDashboard() {
  const { user } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [assoc, setAssoc] = useState(null);
  const [tab, setTab] = useState("overview");

  useEffect(() => {
    if (user === undefined) return;
    if (!user) return navigate("/login");
    if (!["association_admin", "mosque_admin", "moderator"].includes(user.role)) return navigate("/");
    if (user.role === "mosque_admin") setTab("events");
    if (user.role === "moderator") setTab("announcements");
    api.get(`/associations/${user.association_slug}`).then(({ data }) => setAssoc(data)).catch(() => {});
  }, [user, navigate]);

  if (!user || !assoc) return <div className="min-h-screen bg-background" />;

  const visibleTabs = TABS.filter((tb) => tb.roles.includes(user.role));

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 flex flex-col md:flex-row gap-8">
        <aside className="md:w-56 shrink-0">
          <div className="md:sticky md:top-24">
            <p className="font-heading font-bold truncate">{assoc.name}</p>
            <Link to={`/${assoc.slug}`} data-testid="admin-visit-page" className="text-xs text-accent hover:underline flex items-center gap-1 mt-0.5">
              /{assoc.slug} <ExternalLink className="h-3 w-3" />
            </Link>
            <nav className="flex md:flex-col gap-1 mt-6 overflow-x-auto pb-2 md:pb-0" data-testid="admin-sidebar">
              {visibleTabs.map((tb) => (
                <button
                  key={tb.id}
                  data-testid={`admin-tab-${tb.id}`}
                  onClick={() => setTab(tb.id)}
                  className={`flex items-center gap-2.5 px-3 py-2 rounded-md text-sm whitespace-nowrap capitalize transition-colors ${tab === tb.id ? "bg-primary text-primary-foreground font-medium" : "hover:bg-muted text-muted-foreground"}`}
                >
                  <tb.icon className="h-4 w-4" /> {t(tb.label)}
                </button>
              ))}
            </nav>
          </div>
        </aside>
        <main className="flex-1 min-w-0">
          {tab === "overview" && <AdminOverview assoc={assoc} />}
          {tab === "events" && <AdminEvents assoc={assoc} user={user} />}
          {tab === "mosques" && <AdminMosques assoc={assoc} user={user} />}
          {tab === "announcements" && <AdminAnnouncements assoc={assoc} />}
          {tab === "gallery" && <AdminGallery assoc={assoc} />}
          {tab === "members" && <AdminMembers assoc={assoc} user={user} />}
          {tab === "settings" && <AdminSettings assoc={assoc} onUpdate={setAssoc} />}
        </main>
      </div>
    </div>
  );
}
